import { Box } from '@mui/material';
import { Container } from '@mui/system';
import BaseTypography from 'components/typography/BaseTypography';
import { FontsEnum } from 'constants/enums/FontsEnum.ts';
import React from 'react';
import { colorPalette } from 'ui-blocks/ColorPalette';
import { reusableStyles } from 'ui-blocks/ReusableStyles';
import { getRedirectUrlWithParams } from '../../../utils/utils';

export default function TermsAndConditionPage() {
	return (
		<Container
			maxWidth="tabletMid"
			sx={{
				padding: '40px',
			}}
		>
			<Box sx={[reusableStyles.flexColWithGap(33)]}>
				<BaseTypography
					styles={{
						fontSize: '40px',
						fontWeight: 540,
						lineHeight: '53.6px',
						letterSpacing: '0.15px',
						fontFamily: FontsEnum.ZODIAK,
					}}
					color={colorPalette.light3}
				>
					Terms and Conditions
				</BaseTypography>
				<Box sx={[reusableStyles.flexColWithGap(24)]}>
					<Box sx={[reusableStyles.flexColWithGap(4)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							Introduction
						</BaseTypography>
						<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
							Welcome to{' '}
							<span style={{ textDecoration: 'underline' }}>
								<a
									href={getRedirectUrlWithParams('https://app.affogato.ai/')}
									target="_blank"
									rel="noopener noreferrer"
									style={{ textDecoration: 'inherit', color: 'inherit' }}
								>
									Affogato.ai
								</a>
							</span>{' '}
							(&quot;Platform&quot;), a service provided by Walrus Tech Inc. (referred to as
							&quot;Company&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;). These Terms
							and Conditions (&quot;Terms&quot;) govern your use of our Platform, including any
							related websites, services, applications, or software provided by us.
							<br />
							By accessing or using the Platform, you (“user” or “you”) agree to be bound by these
							Terms. If you do not agree with these Terms, you may not use the Platform.
						</BaseTypography>
					</Box>
					<Box sx={[reusableStyles.flexColWithGap(4)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							Eligibility
						</BaseTypography>
						<Box
							component="ul"
							sx={[
								reusableStyles.flexCol,
								{
									color: 'colors.white',
									margin: 0,
									listStyleType: 'disc',
									padding: '0 20px',
									alignItems: 'flex-start',
								},
							]}
						>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Age Requirement:</strong> You must be at least 18 years old to use the
									Platform. By accessing or using the Platform, you represent and warrant that you
									meet this age requirement.
								</BaseTypography>
							</Box>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Authority:</strong> If you are using the Platform on behalf of an entity,
									you represent and warrant that you have the authority to bind that entity to these
									Terms.
								</BaseTypography>
							</Box>
						</Box>
					</Box>
					<Box sx={[reusableStyles.flexColWithGap(4)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							Accounts and Registration
						</BaseTypography>
						<Box
							component="ul"
							sx={[
								reusableStyles.flexCol,
								{
									color: 'colors.white',
									margin: 0,
									listStyleType: 'disc',
									padding: '0 20px',
									alignItems: 'flex-start',
								},
							]}
						>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Account Creation:</strong> To access certain features, you must create an
									account. You agree to provide accurate, current, and complete information during
									registration, and to keep your account information up to date.
								</BaseTypography>
							</Box>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Security:</strong> You are responsible for maintaining the confidentiality
									of your login credentials and for all activities that occur under your account.
									You must notify us immediately of any unauthorized use of your account.
								</BaseTypography>
							</Box>
						</Box>
					</Box>

					<Box sx={[reusableStyles.flexColWithGap(4)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							Plans and Content Ownership
						</BaseTypography>
						<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
							We offer two types of plans on our Platform: Non-Commercial and Commercial. The type
							of plan you choose affects the ownership and usage rights of the content generated on
							our Platform (&quot;Generated Content&quot; or &quot;Content&quot;).
						</BaseTypography>
						<Box
							component="ol"
							sx={[
								reusableStyles.flexColWithGap(12),
								{
									color: 'colors.white',
									margin: 0,
									alignItems: 'flex-start',
									listStyleType: 'lower-alpha',
									fontSize: '12px',
									fontWeight: 600,
								},
							]}
						>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Non-Commercial Plan</strong>
								</BaseTypography>
								<Box
									component="ol"
									sx={[
										reusableStyles.flexCol,
										{
											color: 'colors.white',
											margin: 0,
											alignItems: 'flex-start',
											listStyleType: 'disc',
											padding: '0 20px',
										},
									]}
								>
									<Box component="li">
										<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
											Usage Rights: The Generated Content under the Non-Commercial Plan does not
											belong exclusively to you.
										</BaseTypography>
									</Box>
									<Box component="li">
										<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
											Company Rights: By using the Non-Commercial Plan, you grant us a worldwide,
											non-exclusive, royalty-free, sublicensable, and transferable license to use,
											reproduce, modify, distribute, display, and create derivative works of such
											Generated Content for any purpose, including but not limited to training,
											marketing, demonstrations, and product/service improvements.
										</BaseTypography>
									</Box>
									<Box component="li">
										<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
											No Ownership Granted: You acknowledge that you do not retain exclusive rights
											to the Content created under the Non-Commercial Plan.
										</BaseTypography>
									</Box>
								</Box>
							</Box>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Commercial Plan:</strong>
								</BaseTypography>
								<Box
									component="ol"
									sx={[
										reusableStyles.flexCol,
										{
											color: 'colors.white',
											margin: 0,
											alignItems: 'flex-start',
											listStyleType: 'disc',
											padding: '0 20px',
										},
									]}
								>
									<Box component="li">
										<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
											Usage Rights: The Generated Content under the Commercial Plan belongs to you.
											You retain all right, title, and interest in such Generated Content.
										</BaseTypography>
									</Box>
									<Box component="li">
										<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
											Company Rights: We will not use or share Generated Content from the Commercial
											Plan for any purpose other than providing the service to you. We will not use
											it for our model training, marketing, or any other external purpose, unless
											you grant us explicit written permission.
										</BaseTypography>
									</Box>
									<Box component="li">
										<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
											Limitations: You are solely responsible for ensuring you have the rights to
											any inputs (e.g., text prompts, images) you provide. We have no liability for
											any intellectual property infringement resulting from your inputs or your use
											of the Generated Content.
										</BaseTypography>
									</Box>
								</Box>
							</Box>
						</Box>
					</Box>
					<Box sx={[reusableStyles.flexColWithGap(4)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							Prohibited Content and Activities
						</BaseTypography>
						<Box sx={[reusableStyles.flexCol]}>
							<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
								<strong>NSFW or Explicit Content:</strong> We strictly prohibit any content
								(including text prompts or uploaded images) that is pornographic, sexually explicit,
								or otherwise NSFW as determined in our sole discretion.
							</BaseTypography>
							<Box
								component="ol"
								sx={[
									reusableStyles.flexCol,
									{
										color: 'colors.white',
										margin: 0,
										alignItems: 'flex-start',
										listStyleType: 'disc',
										padding: '0 20px',
									},
								]}
							>
								<Box component="li">
									<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
										Enforcement: We reserve the right to immediately terminate your account if you
										generate or attempt to generate prohibited content. No refunds will be provided
										in such cases.
									</BaseTypography>
								</Box>
								<Box component="li">
									<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
										Reporting: We further reserve the right to report any unlawful or potentially
										unlawful activity to the relevant authorities.
									</BaseTypography>
								</Box>
							</Box>
						</Box>
						<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
							<strong>Illegal or Harmful Activities:</strong> You may not use the Platform to engage
							in any unlawful, misleading, malicious, or discriminatory activities.
						</BaseTypography>
						<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
							<strong>Rights Clearance:</strong> You represent and warrant that you have all
							necessary rights to any content you upload or provide as input to the Platform. We
							assume no responsibility for verifying the ownership of any user-provided content.
						</BaseTypography>
					</Box>
					<Box sx={[reusableStyles.flexColWithGap(4)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							User Responsibilities and Indemnification
						</BaseTypography>
						<Box sx={[reusableStyles.flexCol, { alignItems: 'flex-start' }]}>
							<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
								<strong>User Responsibilities:</strong> You are solely responsible for
							</BaseTypography>
							<Box
								component="ol"
								sx={[
									reusableStyles.flexCol,
									{
										color: 'colors.white',
										margin: 0,
										alignItems: 'flex-start',
										listStyleType: 'disc',
										padding: '0 20px',
									},
								]}
							>
								<Box component="li">
									<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
										The accuracy, legality, and appropriateness of any content or inputs you
										provide.
									</BaseTypography>
								</Box>
								<Box component="li">
									<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
										The results of any outputs generated by the Platform.
									</BaseTypography>
								</Box>
								<Box component="li">
									<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
										Compliance with all applicable laws and regulations when using the Platform.
									</BaseTypography>
								</Box>
							</Box>
						</Box>
						<Box sx={[reusableStyles.flexCol, { alignItems: 'flex-start', marginTop: '16px' }]}>
							<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
								<strong>Indemnification:</strong> You agree to defend, indemnify, and hold harmless
								the Company, its affiliates, directors, officers, employees, and agents from and
								against any and all claims, damages, obligations, losses, liabilities, costs, or
								debt, and expenses (including reasonable attorneys&quot; fees) arising from:
							</BaseTypography>
							<Box
								component="ol"
								sx={[
									reusableStyles.flexCol,
									{
										color: 'colors.white',
										margin: 0,
										alignItems: 'flex-start',
										listStyleType: 'disc',
										padding: '0 20px',
									},
								]}
							>
								<Box component="li">
									<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
										Your use of and access to the Platform.
									</BaseTypography>
								</Box>
								<Box component="li">
									<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
										Your violation of any term of these Terms.
									</BaseTypography>
								</Box>
								<Box component="li">
									<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
										Your violation of any third-party right, including without limitation any
										intellectual property, property, or privacy right.
									</BaseTypography>
								</Box>
								<Box component="li">
									<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
										Any claim that your content caused damage to a third party.
									</BaseTypography>
								</Box>
							</Box>
						</Box>
					</Box>
					<Box sx={[reusableStyles.flexColWithGap(4)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							Disclaimers and Limitation of Liability
						</BaseTypography>
						<Box sx={[reusableStyles.flexCol, { alignItems: 'flex-start' }]}>
							<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
								<strong>No Warranties:</strong> The Platform is provided on an &quot;AS IS&quot; and
								&quot;AS AVAILABLE&quot; basis. To the fullest extent permitted by law, we disclaim
								all warranties, whether express or implied, including but not limited to implied
								warranties of merchantability, fitness for a particular purpose, title, and
								non-infringement.
							</BaseTypography>
						</Box>
						<Box sx={[reusableStyles.flexCol, { alignItems: 'flex-start' }]}>
							<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
								<strong>Limitation of Liability:</strong> To the maximum extent permitted by law,
								the Company shall not be liable for any indirect, incidental, special,
								consequential, or punitive damages, including without limitation, loss of profits,
								data, use, goodwill, or other intangible losses resulting from:
							</BaseTypography>
							<Box
								component="ol"
								sx={[
									reusableStyles.flexCol,
									{
										color: 'colors.white',
										margin: 0,
										alignItems: 'flex-start',
										listStyleType: 'disc',
										padding: '0 20px',
									},
								]}
							>
								<Box component="li">
									<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
										Your access to or use of (or inability to access or use) the Platform;
									</BaseTypography>
								</Box>
								<Box component="li">
									<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
										Any content obtained from the Platform; or
									</BaseTypography>
								</Box>
								<Box component="li">
									<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
										Unauthorized access, use, or alteration of your transmissions or content.
									</BaseTypography>
								</Box>
							</Box>
							<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
								In no event shall the Company&apos;s total liability exceed the amount you paid to
								us for using the Platform (if any) or one hundred U.S. dollars ($100), whichever is
								greater.
							</BaseTypography>
						</Box>
					</Box>
					<Box sx={[reusableStyles.flexColWithGap(4)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							Termination
						</BaseTypography>
						<Box
							component="ol"
							sx={[
								reusableStyles.flexCol,
								{
									color: 'colors.white',
									margin: 0,
									alignItems: 'flex-start',
									listStyleType: 'disc',
									padding: '0 20px',
								},
							]}
						>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Termination by You:</strong> You may terminate your account at any time by
									contacting us or using the account settings within the Platform.
								</BaseTypography>
							</Box>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Termination by Us:</strong> We may suspend or terminate your account if
									you violate these Terms or engage in any activity that may harm us or other users.
								</BaseTypography>
							</Box>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Effect of Termination:</strong> Upon termination, you must cease all use
									of the Platform. Any licenses or rights granted to us under the Non-Commercial
									Plan survive termination.
								</BaseTypography>
							</Box>
						</Box>
					</Box>
					<Box sx={[reusableStyles.flexColWithGap(4)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							Refund Policy
						</BaseTypography>
						<Box
							component="ol"
							sx={[
								reusableStyles.flexCol,
								{
									color: 'colors.white',
									margin: 0,
									alignItems: 'flex-start',
									listStyleType: 'disc',
									padding: '0 20px',
								},
							]}
						>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>General No Refund Policy</strong>
									<br />
									All payments made for Affogato.ai services, including but not limited to
									subscriptions, prepaid usage credits, and any other digital services, are final
									and non-refundable. By purchasing a subscription or usage credits, you acknowledge
									and agree that Affogato.ai does not provide refunds, prorated refunds, or credits
									for any unused time, unused credits, or partial service usage.
								</BaseTypography>
							</Box>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Subscription Fees</strong>
									<br />
									If you subscribe to a paid plan, your subscription fee is charged at the beginning
									of the billing cycle and is non-refundable. If you cancel your subscription, you
									will retain access to the service for the remainder of the current billing period,
									and no refund will be provided for the unused portion of your subscription.
								</BaseTypography>
							</Box>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Extra Credits usage</strong>
									<br />
									Unused credits cannot be transferred, exchanged for cash, or refunded under any
									circumstances. Please ensure you purchase the appropriate amount of credits based
									on your needs.
								</BaseTypography>
							</Box>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Mandatory Refund Exceptions (EU, UK, Turkey)</strong>
									<br />
									If you are a resident of the European Union (EU), United Kingdom (UK), or Turkey,
									you may have the legal right to request a refund for a subscription within 14 days
									of purchase, provided you have not used the service beyond the minimum trial usage
									(if applicable). Refunds will not be granted if you have actively used the service
									during this period. Refund requests under these laws must be submitted to
									hello@affogato.ai within the 14-day window.
								</BaseTypography>
							</Box>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Chargebacks and Disputes</strong>
									<br />
									Initiating a chargeback or dispute with your payment provider without first
									contacting Affogato.ai support may result in suspension or termination of your
									account. If you believe you were charged incorrectly, please contact us at
									hello@affogato.ai to resolve the issue before escalating to a dispute.
								</BaseTypography>
							</Box>
						</Box>
					</Box>
					<Box sx={[reusableStyles.flexColWithGap(4)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							Governing Law and Dispute Resolution
						</BaseTypography>
						<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
							These Terms will be governed by and construed in accordance with the laws of the State
							of Delaware, without regard to its conflict of laws principles. You agree to submit to
							the personal jurisdiction of the federal and state courts located in Delaware for the
							purpose of litigating any claims or disputes.
						</BaseTypography>
					</Box>
					<Box sx={[reusableStyles.flexColWithGap(4)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							Changes to the Terms
						</BaseTypography>
						<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
							We reserve the right to modify these Terms at any time. If we make material changes,
							we will notify you by posting a notice on our Platform or sending you an email. Your
							continued use of the Platform after such changes are posted constitutes your
							acceptance of the revised Terms.
						</BaseTypography>
					</Box>
					<Box sx={[reusableStyles.flexColWithGap(4)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							Miscellaneous
						</BaseTypography>
						<Box
							component="ol"
							sx={[
								reusableStyles.flexCol,
								{
									color: 'colors.white',
									margin: 0,
									alignItems: 'flex-start',
									listStyleType: 'disc',
									padding: '0 20px',
								},
							]}
						>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Severability:</strong> If any provision of these Terms is held invalid or
									unenforceable, the remaining provisions will remain in full force and effect.
								</BaseTypography>
							</Box>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>No Waiver:</strong> Our failure to enforce any right or provision of these
									Terms will not be considered a waiver of those rights.
								</BaseTypography>
							</Box>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Entire Agreement:</strong> These Terms, along with any referenced
									documents, constitute the entire agreement between you and the Company regarding
									the Platform.
								</BaseTypography>
							</Box>
						</Box>
					</Box>
					<Box sx={[reusableStyles.flexColWithGap(4)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							Contact Us
						</BaseTypography>
						<Box sx={[reusableStyles.flexCol, { alignItems: 'flex-start' }]}>
							<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
								If you have any questions or concerns regarding these Terms and Conditions, please
								contact us at:
							</BaseTypography>
							<Box
								component="ul"
								sx={[
									reusableStyles.flexCol,
									{
										color: 'colors.white',
										margin: 0,
										listStyleType: 'disc',
										padding: '0 20px',
										alignItems: 'flex-start',
									},
								]}
							>
								<Box component="li">
									<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
										Walrus Tech Inc.
									</BaseTypography>
								</Box>
								<Box component="li">
									<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
										66 Franklin St, Suite 300, Oakland, CA 94607
									</BaseTypography>
								</Box>
								<Box component="li">
									<a
										href="mailto:hello@affogato.ai"
										style={{ textDecoration: 'underline', color: 'inherit' }}
									>
										<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
											hello@affogato.ai
										</BaseTypography>
									</a>
								</Box>
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>
		</Container>
	);
}
